import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { randomNewsSlug } from "@/lib/slug";
import { uploadPublicImage } from "@/lib/upload";
import { usePortalUserContext } from "@/admin/PortalUserContext";
import { postgrestErrorMessage } from "@/lib/postgrestErrorMessage";
import { formatNewsMonthYear } from "@/lib/displayOrderDates";
import { AdminImagePicker } from "@/components/admin/AdminImagePicker";
import { adminListRowHighlightClass } from "@/lib/adminListRowHighlight";
import {
  adminAddNewSecondaryButtonClass,
  adminFormSectionClass,
  adminListSectionClass,
} from "@/lib/adminSectionChrome";
import {
  lockedScopeFromAssignment,
  pickActiveScopedAssignment,
  portalHasSiteWideWrite,
} from "@/lib/portalSession";

type ParentKind = "branch" | "batch" | "association" | "oba";
type Opt = { id: string; label: string };

type Row = {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  slug: string | null;
  published_at: string | null;
  created_at?: string;
  updated_at?: string;
  branch_id?: string | null;
  batch_id?: string | null;
  association_id?: string | null;
  branches?: { name: string } | null;
  batches?: {
    custom_title: string | null;
    official_title: string | null;
    slug: string;
  } | null;
  associations?: { name: string } | null;
};

type FilterOwner = "all" | "oba" | "branch" | "batch" | "association";

function firstEmbed<T>(v: T | T[] | null | undefined): T | null {
  if (v == null) return null;
  return Array.isArray(v) ? (v[0] ?? null) : v;
}

function normalizeNewsRow(raw: Record<string, unknown>): Row {
  const branches = firstEmbed(raw.branches as { name: string } | { name: string }[] | null);
  const batches = firstEmbed(
    raw.batches as
      | { custom_title: string | null; official_title: string | null; slug: string }
      | Array<{ custom_title: string | null; official_title: string | null; slug: string }>
      | null
  );
  const associations = firstEmbed(raw.associations as { name: string } | { name: string }[] | null);
  return {
    ...(raw as Omit<Row, "branches" | "batches" | "associations">),
    created_at: String((raw as { created_at?: string }).created_at ?? ""),
    branches,
    batches,
    associations,
  };
}

function newsListParentLabel(r: Row): string {
  if (r.batch_id && r.batches) {
    return r.batches.custom_title || r.batches.official_title || r.batches.slug;
  }
  if (r.branch_id && r.branches) return r.branches.name;
  if (r.association_id && r.associations) return r.associations.name;
  if (!r.branch_id && !r.batch_id && !r.association_id) return "OBA";
  return "—";
}

export function NewsTab() {
  const portal = usePortalUserContext();
  const siteWide = portal ? portalHasSiteWideWrite(portal.grants) : false;
  const activeScoped = portal
    ? pickActiveScopedAssignment(portal.assignments, portal.activeAssignmentId)
    : null;
  const lockedScope = portal && !siteWide ? lockedScopeFromAssignment(activeScoped) : null;
  const lockedKind: ParentKind | null = lockedScope?.lockedKind ?? null;
  const lockedParentId = lockedScope?.lockedParentId ?? null;

  const [rows, setRows] = useState<Row[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [filterOwner, setFilterOwner] = useState<FilterOwner>("all");
  const [filterSearch, setFilterSearch] = useState("");

  const [editingId, setEditingId] = useState<string | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [formNotice, setFormNotice] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const [listNotice, setListNotice] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [kind, setKind] = useState<ParentKind>(lockedKind ?? "oba");
  const [parents, setParents] = useState<Opt[]>([]);
  const [parentId, setParentId] = useState(lockedParentId ?? "");
  useEffect(() => {
    if (lockedKind) setKind(lockedKind);
    if (lockedParentId) setParentId(lockedParentId);
  }, [lockedKind, lockedParentId]);

  const loadParents = useCallback(async () => {
    if (kind === "oba") {
      setParents([]);
      return;
    }
    if (!lockedParentId) setParentId("");
    if (kind === "branch") {
      const { data } = await supabase.from("branches").select("id,name").order("name");
      setParents(
        (data ?? []).map((r: { id: string; name: string }) => ({
          id: r.id,
          label: r.name,
        }))
      );
      if (lockedParentId && lockedKind === "branch") setParentId(lockedParentId);
    } else if (kind === "batch") {
      const { data } = await supabase
        .from("batches")
        .select("id,custom_title,official_title,slug")
        .order("al_year", { ascending: false, nullsFirst: false });
      setParents(
        (data ?? []).map(
          (r: { id: string; custom_title: string | null; official_title: string | null; slug: string }) => ({
            id: r.id,
            label: r.custom_title || r.official_title || r.slug,
          })
        )
      );
      if (lockedParentId && lockedKind === "batch") setParentId(lockedParentId);
    } else {
      const { data } = await supabase.from("associations").select("id,name").order("name");
      setParents(
        (data ?? []).map((r: { id: string; name: string }) => ({
          id: r.id,
          label: r.name,
        }))
      );
      if (lockedParentId && lockedKind === "association") setParentId(lockedParentId);
    }
  }, [kind, lockedKind, lockedParentId]);

  const load = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    let q = supabase
      .from("news")
      .select(
        "id,title,description,image_url,slug,published_at,created_at,updated_at,branch_id,batch_id,association_id,branches(name),batches(custom_title,official_title,slug),associations(name)"
      )
      .order("updated_at", { ascending: false });

    if (!siteWide && lockedKind && lockedParentId) {
      if (lockedKind === "branch") q = q.eq("branch_id", lockedParentId);
      else if (lockedKind === "batch") q = q.eq("batch_id", lockedParentId);
      else q = q.eq("association_id", lockedParentId);
    }

    const { data, error } = await q;
    if (error) {
      setListError(postgrestErrorMessage(error));
      setRows([]);
    } else {
      setRows(((data ?? []) as Record<string, unknown>[]).map(normalizeNewsRow));
    }
    setListLoading(false);
  }, [lockedKind, lockedParentId, siteWide]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadParents();
  }, [loadParents]);

  const displayedRows = useMemo(() => {
    let list = rows;
    if (siteWide) {
      if (filterOwner === "oba") {
        list = list.filter((r) => !r.branch_id && !r.batch_id && !r.association_id);
      } else if (filterOwner === "branch") {
        list = list.filter((r) => Boolean(r.branch_id));
      } else if (filterOwner === "batch") {
        list = list.filter((r) => Boolean(r.batch_id));
      } else if (filterOwner === "association") {
        list = list.filter((r) => Boolean(r.association_id));
      }
      const q = filterSearch.trim().toLowerCase();
      if (q) {
        list = list.filter(
          (r) =>
            r.title.toLowerCase().includes(q) ||
            (r.description?.toLowerCase().includes(q) ?? false) ||
            newsListParentLabel(r).toLowerCase().includes(q)
        );
      }
    }
    return list;
  }, [rows, siteWide, filterOwner, filterSearch]);

  function clearFormNoticeSoon() {
    window.setTimeout(() => {
      setFormNotice((n) => (n?.kind === "success" ? null : n));
    }, 4500);
  }

  function startNew() {
    setEditingId(null);
    setExistingImageUrl(null);
    setTitle("");
    setDescription("");
    setFile(null);
    setFormNotice(null);
    if (lockedKind) {
      setKind(lockedKind);
      if (lockedParentId) setParentId(lockedParentId);
    } else if (siteWide) {
      setKind("oba");
      setParentId("");
    }
  }

  function startEdit(r: Row) {
    setEditingId(r.id);
    setTitle(r.title);
    setDescription(r.description ?? "");
    setExistingImageUrl(r.image_url);
    setFile(null);
    setFormNotice(null);
    if (!siteWide && lockedKind && lockedParentId) {
      setKind(lockedKind);
      setParentId(lockedParentId);
      return;
    }

    if (r.branch_id) {
      setKind("branch");
      setParentId(r.branch_id);
    } else if (r.batch_id) {
      setKind("batch");
      setParentId(r.batch_id);
    } else if (r.association_id) {
      setKind("association");
      setParentId(r.association_id);
    } else {
      setKind("oba");
      setParentId("");
    }
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    setFormNotice(null);
    setListNotice(null);
    if (kind !== "oba" && !parentId) {
      setFormNotice({ kind: "error", text: "Select an owner (branch, batch, or association)." });
      return;
    }
    if (kind === "oba" && !siteWide) {
      setFormNotice({
        kind: "error",
        text: "Only portal managers or site-wide admins can publish OBA-wide news.",
      });
      return;
    }
    setSaving(true);
    try {
      let newImageUrl: string | null = null;
      if (file) newImageUrl = await uploadPublicImage(file, "news");

      const image_url = file
        ? newImageUrl
        : editingId
          ? existingImageUrl?.trim() || null
          : null;

      const base = {
        title: title.trim(),
        description: description || null,
        image_url,
        branch_id: kind === "branch" ? parentId : null,
        batch_id: kind === "batch" ? parentId : null,
        association_id: kind === "association" ? parentId : null,
      };

      if (!base.title) {
        setFormNotice({ kind: "error", text: "Title is required." });
        return;
      }

      if (editingId) {
        const { error } = await supabase.from("news").update(base).eq("id", editingId);
        if (error) throw error;
        setFormNotice({ kind: "success", text: "News saved successfully." });
        clearFormNoticeSoon();
      } else {
        const { error } = await supabase.from("news").insert({
          ...base,
          slug: randomNewsSlug(),
        });
        if (error) throw error;
        setFormNotice({ kind: "success", text: "News published successfully." });
        clearFormNoticeSoon();
      }

      setFile(null);
      await load();
      startNew();
    } catch (err) {
      console.error(err);
      setFormNotice({
        kind: "error",
        text: postgrestErrorMessage(err),
      });
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(id: string) {
    if (!confirm("Delete this news item? This cannot be undone.")) return;
    setListNotice(null);
    setDeletingId(id);
    try {
      const { error } = await supabase.from("news").delete().eq("id", id);
      if (error) throw error;
      setListNotice({ kind: "success", text: "News deleted successfully." });
      window.setTimeout(() => setListNotice((n) => (n?.kind === "success" ? null : n)), 4000);
      await load();
      if (editingId === id) startNew();
    } catch (err) {
      console.error(err);
      setListNotice({
        kind: "error",
        text: `Could not delete: ${postgrestErrorMessage(err)}`,
      });
    } finally {
      setDeletingId(null);
    }
  }

  const noticeClass = (kind: "success" | "error") =>
    kind === "success"
      ? "rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
      : "rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900";

  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-brand-blue">News</h2>
      <p className="mt-2 text-sm text-brand-blue/80">
        Publish news for{" "}
        <strong className="font-semibold">OBA</strong> (site-wide) or attach to a branch, batch, or association.
      </p>

      <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-start">
        <section aria-labelledby="news-list-heading" className={adminListSectionClass}>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-brand-gray pb-3">
            <h3 id="news-list-heading" className="font-display text-lg font-bold text-brand-blue">
              News list
            </h3>
            <button
              type="button"
              onClick={() => {
                if (!saving && editingId) startNew();
              }}
              disabled={saving || !editingId}
              title={
                !editingId
                  ? "You are already adding new news — use the form on the right"
                  : "Clear the editor and add a new news item"
              }
              className={adminAddNewSecondaryButtonClass}
            >
              Add new news
            </button>
          </div>
          {siteWide ? (
            <div className="mt-4 rounded-lg border border-brand-gray/80 bg-brand-gray/20 p-3">
              <div className="flex flex-wrap items-end gap-3">
                <label className="text-xs font-semibold text-brand-blue">
                  Scope
                  <select
                    value={filterOwner}
                    onChange={(e) => setFilterOwner(e.target.value as FilterOwner)}
                    className="mt-1 block rounded border border-brand-gray bg-white px-2 py-1.5 text-sm"
                  >
                    <option value="all">All</option>
                    <option value="oba">OBA only</option>
                    <option value="branch">Branch</option>
                    <option value="batch">Batch</option>
                    <option value="association">Association</option>
                  </select>
                </label>
                <label className="min-w-0 flex-1 text-xs font-semibold text-brand-blue">
                  Search list
                  <input
                    type="search"
                    value={filterSearch}
                    onChange={(e) => setFilterSearch(e.target.value)}
                    placeholder="Title, description, or batch…"
                    className="mt-1 block w-full rounded border border-brand-gray bg-white px-3 py-2 text-sm"
                  />
                </label>
              </div>
              <p className="mt-2 text-xs text-brand-blue/70">
                Showing {displayedRows.length} of {rows.length}
                {filterSearch.trim() || filterOwner !== "all" ? " (filtered)" : ""}.
              </p>
            </div>
          ) : null}
          {listNotice ? (
            <div className="mt-3" role="status">
              <div className={noticeClass(listNotice.kind)}>{listNotice.text}</div>
            </div>
          ) : null}
          {listLoading ? (
            <p className="mt-4 text-sm text-brand-blue/60">Loading news…</p>
          ) : listError ? (
            <p className="mt-4 text-sm text-red-800">{listError}</p>
          ) : (
            <div className="mt-4 max-h-[min(70vh,36rem)] overflow-auto rounded-lg border border-brand-gray/80 bg-brand-gray/30 p-2">
              <ul className="space-y-1">
                {displayedRows.length === 0 ? (
                  <li className="px-2 py-4 text-sm text-brand-blue/60">No items match the current filters.</li>
                ) : null}
                {displayedRows.map((r) => {
                  const active = editingId === r.id;
                  const when = formatNewsMonthYear(r.updated_at ?? r.created_at);
                  return (
                    <li
                      key={r.id}
                      className={`flex items-start justify-between gap-3 rounded-lg px-3 py-3 text-sm transition-colors ${adminListRowHighlightClass(active)}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold leading-snug text-brand-blue">{r.title}</p>
                        <p className="mt-1 text-xs leading-relaxed text-brand-blue/50">
                          {newsListParentLabel(r)}
                          {when ? (
                            <>
                              <span className="text-brand-blue/35"> · </span>
                              {when}
                            </>
                          ) : null}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-row items-center gap-4 pt-0.5">
                        <button
                          type="button"
                          onClick={() => startEdit(r)}
                          disabled={deletingId === r.id}
                          className="text-xs font-bold uppercase tracking-wide text-brand-maroon disabled:opacity-50"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => void onDelete(r.id)}
                          disabled={deletingId === r.id}
                          className="text-xs font-bold uppercase tracking-wide text-red-700 disabled:opacity-50"
                        >
                          {deletingId === r.id ? "Deleting…" : "Delete"}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </section>

        <section aria-labelledby="news-form-heading" className={adminFormSectionClass}>
          <div className="border-b border-brand-gray pb-3">
            <h3 id="news-form-heading" className="font-display text-lg font-bold text-brand-blue">
              {editingId ? "Edit news" : "Add new news"}
            </h3>
            {editingId && title.trim() ? (
              <p className="mt-1 text-sm text-brand-blue/65 line-clamp-2">&ldquo;{title.trim()}&rdquo;</p>
            ) : null}
          </div>

          <div className="mt-4 flex flex-wrap gap-4">
            <label className="text-sm font-semibold text-brand-blue">
              Owner type
              <select
                value={kind}
                onChange={(e) => {
                  const v = e.target.value as ParentKind;
                  setKind(v);
                  if (v === "oba") setParentId("");
                }}
                disabled={!siteWide && Boolean(lockedKind)}
                className="ml-2 rounded border border-brand-gray px-2 py-1 disabled:opacity-60"
              >
                <option value="branch">Branch</option>
                <option value="batch">Batch</option>
                <option value="association">Association</option>
                {siteWide ? <option value="oba">OBA (site-wide)</option> : null}
              </select>
            </label>

            {kind === "oba" ? (
              <p className="self-end text-sm text-brand-blue/75">
                OBA news appears on the Home page and on the public <span className="font-semibold">OBA News</span>{" "}
                page.
              </p>
            ) : (
              <label className="text-sm font-semibold text-brand-blue">
                Owner
                <select
                  value={parentId}
                  onChange={(e) => setParentId(e.target.value)}
                  disabled={!siteWide && Boolean(lockedKind)}
                  className="ml-2 max-w-xs rounded border border-brand-gray px-2 py-1 disabled:opacity-60"
                >
                  <option value="">— Select —</option>
                  {parents.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {lockedKind && !siteWide ? (
            <p className="mt-2 text-xs text-brand-blue/70">
              You can publish news for your assigned {lockedKind.replace("_", " ")} only.
            </p>
          ) : null}

          <form onSubmit={onSave} className="mt-5 space-y-4" aria-busy={saving}>
            {formNotice ? (
              <div role="status" className={noticeClass(formNotice.kind)}>
                {formNotice.text}
              </div>
            ) : null}
            <div className="space-y-1">
              <label htmlFor="news-title" className="block text-xs font-semibold text-brand-blue">
                Title
              </label>
              <input
                id="news-title"
                required
                autoComplete="off"
                placeholder="Headline"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded border border-brand-gray px-3 py-2 text-sm"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="news-description" className="block text-xs font-semibold text-brand-blue">
                Description
              </label>
              <textarea
                id="news-description"
                placeholder="Summary or body text"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full rounded border border-brand-gray px-3 py-2 text-sm"
              />
            </div>
            <AdminImagePicker
              label="Image"
              inputId="news-image"
              remoteUrl={existingImageUrl}
              selectedFile={file}
              onFileChange={setFile}
              onClear={() => {
                setFile(null);
                setExistingImageUrl(null);
              }}
              disabled={saving}
            />
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="submit"
                disabled={saving}
                className="rounded-full bg-brand-blue px-6 py-2 text-sm font-bold uppercase text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? (editingId ? "Saving…" : "Publishing…") : editingId ? "Save" : "Publish"}
              </button>
              <button
                type="button"
                onClick={startNew}
                disabled={saving}
                className="rounded-full border border-brand-gray px-4 py-2 text-sm disabled:opacity-60"
              >
                Clear
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
